import Constant from '@/constant';
import cookies from '@/utilities/cookie.js';

var PURCHASE_INFO_KEY = 'mallPurchaseInfo';

// 구매정보 시트의 경감 대상 → 수급권 구분 코드(recipient_type_code)
// 서버: C/E/F = 차상위 → 본인부담 0%, 알 수 없는 코드는 400
// TODO: 기초생활 수급자 코드와 차상위(C/E/F) 중 보낼 값을 서버와 확인
var RECIPIENT_TYPE_CODES = {
    none: null,
    basic: null,
    nearPoor: 'C',
};

// 상품 이미지가 없을 때(image_url null, 판매 종료 등) 기본 이미지
var DEFAULT_PRODUCT_IMAGE = 'https://d2lhbt4yi836ng.cloudfront.net/item/default.png';

// 프로필 질환 유형(diabetes_type) 코드 → 화면 표시 (마이페이지 홈과 같은 표기)
var DIABETES_TYPE_NAMES = {
    TYPE1: '1형당뇨',
    TYPE2: '2형당뇨',
    GESTATIONAL: '임신성당뇨(투여)',
    GESTATIONAL_WITHOUT_INSULIN: '임신성당뇨(미투여)',
    RARE_INTRACTABLE: '희귀난치성질환',
    SELF_CATHETER: '자가도뇨 카테터',
};

export default {
    // 로그인 토큰 (없으면 null)
    getAccessToken: function()
    {
        var token = cookies.getLocalStorage('access_token');
        return token ? token : null;
    },
    // 토큰이 없거나 만료됐을 때 로그인 화면으로 (로그인 후 /mall 로 돌아옴)
    goLogin: function(router)
    {
        cookies.deleteLocalStorage('access_token');
        router.push({ path: '/', query: { returnUrl: 'mall' } });
    },
    isAuthError: function(err)
    {
        return !!err && (err.status === 401 || err.status === 403);
    },
    errorMessage: function(err)
    {
        return err && err.message ? err.message : '고객센터에 문의 바랍니다';
    },

    // 프로필별 구매정보 { prescriptionDays, reductionType, usesInsulinPump }
    // 저장 API가 없어 기기(localStorage)에 보관
    getPurchaseInfo: function(profileId)
    {
        try {
            var saved = JSON.parse(cookies.getLocalStorage(PURCHASE_INFO_KEY) || '{}');
            return saved[profileId] || null;
        } catch (e) {
            return null;
        }
    },
    setPurchaseInfo: function(profileId, info)
    {
        var saved = {};
        try {
            saved = JSON.parse(cookies.getLocalStorage(PURCHASE_INFO_KEY) || '{}');
        } catch (e) {
            saved = {};
        }
        saved[profileId] = info;
        cookies.setLocalStorage(PURCHASE_INFO_KEY, JSON.stringify(saved));
    },
    // 처방일수 (없으면 null - 서버는 요양비 한도 0으로 계산)
    getDateCount: function(info)
    {
        var days = info ? Number(info.prescriptionDays) : 0;
        return days > 0 ? days : null;
    },
    getRecipientTypeCode: function(info)
    {
        if (!info || !info.reductionType) return null;
        return RECIPIENT_TYPE_CODES[info.reductionType] || null;
    },

    // 프로필 한 건 { profile, delegation_info, addition_info, card_info }
    // FETCH_PROFILE은 프로필 목록 state를 단건으로 덮어써서 목록을 받아 찾음
    fetchProfile: function(store, profileId, token)
    {
        return store.dispatch(Constant.FETCH_PROFILES, { token: token })
            .then((profiles) => {
                return (profiles || []).find((item) => item.profile.id === profileId) || null;
            });
    },

    // 카테고리 + 전체 상품
    // 장바구니·주문은 상품 id·이름만 주므로 상품 정보(이미지·가격·카테고리)를 여기서 찾음
    fetchCatalog: function(store, profileId, token)
    {
        return store.dispatch(Constant.FETCH_STORE_CATEGORIES, { profile_id: profileId, token: token })
            .then((data) => {
                var categories = (data.categories || []).slice().sort((a, b) => a.order - b.order);
                var requests = categories.map((category) => {
                    return store.dispatch(Constant.FETCH_STORE_PRODUCTS, { profile_id: profileId, category_id: category.id, token: token });
                });

                return Promise.all(requests).then((results) => {
                    var products = [];
                    var productMap = {};
                    results.forEach((result) => {
                        products = products.concat((result && result.products) || []);
                    });
                    products.forEach((product) => {
                        productMap[product.id] = product;
                    });
                    return {
                        categories: categories,
                        defaultCategoryId: data.default_category_id,
                        products: products,
                        productMap: productMap,
                    };
                });
            });
    },

    // 상품 카드 표시용 (보험적용가가 원가보다 낮으면 원가 대비 할인율로 표시)
    toProductView: function(product, categories)
    {
        var insuredPrice = product.insured_price;
        var hasDiscount = insuredPrice !== null && insuredPrice !== undefined && insuredPrice < product.price;
        var category = (categories || []).find((item) => item.id === product.category_id);

        return {
            id: product.id,
            category: product.category_id,
            categoryLabel: category ? category.name : '',
            name: product.name,
            image: product.image_url || DEFAULT_PRODUCT_IMAGE,
            price: hasDiscount ? insuredPrice : product.price,
            originalPrice: hasDiscount ? product.price : undefined,
            discountRate: hasDiscount ? Math.round((1 - insuredPrice / product.price) * 100) : undefined,
            maxCount: product.max_count || null,
        };
    },

    // 상품 이미지 (없으면 기본 이미지)
    productImage: function(imageUrl)
    {
        return imageUrl || DEFAULT_PRODUCT_IMAGE;
    },

    // 질환 유형 한글 표시 (코드가 아니면 받은 값 그대로)
    diseaseTypeName: function(diabetesType)
    {
        if (!diabetesType) return '';
        return DIABETES_TYPE_NAMES[diabetesType] || diabetesType;
    },

    // 헤더 뒤로가기: history를 따르지 않고 정해진 이전 화면으로 이동 (replace라 기록이 쌓이지 않음)
    // 들어온 화면 주소(query.returnTo)가 있으면 그 화면으로 돌아감
    goBack: function(router, route, fallback)
    {
        var returnTo = route.query.returnTo;
        if (typeof returnTo === 'string' && returnTo.indexOf('/mall/') === 0) {
            router.replace(returnTo);
            return;
        }
        router.replace(fallback);
    },

    // 상품별 최대 구매 수량(max_count)을 넘게 담으려 할 때 안내 문구
    maxCountMessage: function(maxCount)
    {
        return '해당 제품은 최대 ' + maxCount + '개까지만 구매 가능합니다.';
    },

    // 마스킹된 카드번호 '40330200****320*' → '4033-0200-****-320*' (이미 '-'가 있으면 그대로)
    formatCardNumber: function(cardNumber)
    {
        var value = String(cardNumber || '');
        if (value.indexOf('-') > -1) return value;
        return value.replace(/(.{4})(?=.)/g, '$1-');
    },
    // '2026-10-01T09:00:00' → '2026.10.01'
    formatDate: function(value)
    {
        if (!value) return '';
        return String(value).slice(0, 10).replace(/-/g, '.');
    },
    // 주민번호 앞자리(YYMMDD-G) → 'YYYY.MM.DD'
    formatBirth: function(registrationNumber)
    {
        var digits = String(registrationNumber || '').replace(/[^0-9]/g, '');
        if (digits.length < 7) return '';
        var century = ['1', '2', '5', '6'].indexOf(digits[6]) > -1 ? '19' : '20';
        return century + digits.slice(0, 2) + '.' + digits.slice(2, 4) + '.' + digits.slice(4, 6);
    },
}



// WEBPACK FOOTER //
// ./src/utilities/mall.js